import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import "../styles/footer.css";
import icon from "../assets/images/ys.svg";

export default function Footer({ lang = "EN", visible }) {
  const [contactClicked, setContactClicked] = useState(false);
  const [linkedinClicked, setLinkedinClicked] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  // If visible prop is controlled externally (homepage), use it directly
  // Otherwise self-detect via window scroll
  useEffect(() => {
    if (visible !== undefined) {
      setIsVisible(visible);
      return;
    }

    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
      const atBottom = window.innerHeight + scrollTop >= scrollHeight - 10;
      setIsVisible(atBottom);
    };

    document.addEventListener("scroll", handleScroll, true);
    return () => document.removeEventListener("scroll", handleScroll, true);
  }, [visible]);

  useEffect(() => {
    // Listen for hash changes to handle contact scrolling
    const handleHashChange = () => {
      if (window.location.hash === "#contact") {
        handleContactClick();
      }
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const content = {
    EN: {
      contact: "CONTACT US",
      linkedin: "CONNECT ON LINKEDIN",
      // pricing: "PRICING",
      articles: "ARTICLES",
      terms: "TERMS & CONDITIONS",
      home: "HOME",
      portfolio: "PORTFOLIO",
      cookies: "COOKIE SETTINGS",
    },
    FR: {
      contact: "CONTACTEZ-NOUS",
      linkedin: "CONNECTEZ SUR LINKEDIN",
      // pricing: "TARIFS",
      articles: "ARTICLES",
      terms: "CONDITIONS GÉNÉRALES",
      home: "ACCUEIL",
      portfolio: "PORTFOLIO",
      cookies: "GESTION DES COOKIES",
    },
  };

  const currentContent = content[lang] || content["EN"];

  const handleContactClick = () => {
    setContactClicked(true);
    setTimeout(() => setContactClicked(false), 400);

    // Scroll to contact section
    const contactElement =
      document.querySelector(".contact-section") ||
      document.querySelector(".contact");
    if (contactElement) {
      contactElement.scrollIntoView({ behavior: "smooth" });
    } else {
      // If not on a page with contact, navigate to home with contact hash
      window.location.href = "/contactYoru";
    }
  };

  const handleLinkedInClick = () => {
    setLinkedinClicked(!linkedinClicked);
    window.open("https://linkedin.com/company/yoru-solutions", "_blank");
  };

  const footerMarkup = (
    <footer className={`footer-container${isVisible ? " footer-visible" : ""}`}>
      <div className="footer-content">
        <div className="footer-left">
          <div className="footer-icon-container">
            <Link to="/" className="footer-logo" title="Go to homepage">
              <img src={icon} alt="Yoru Solutions" className="footer-icon" width="80" height="80" loading="lazy" />
            </Link>
          </div>

          <div className="footer-links">
            <Link to="/" className="footer-link">
              {currentContent.home}
            </Link>
            <Link to="/portfolio" className="footer-link">
              {currentContent.portfolio}
            </Link>
            <Link to="/articles" className="footer-link">
              {currentContent.articles}
            </Link>
            <Link to="/terms" className="footer-link">
              {currentContent.terms}
            </Link>
            <button
              className="footer-link footer-cookie-btn"
              onClick={() => window.openCookieBanner && window.openCookieBanner()}
            >
              {currentContent.cookies}
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="footer-right">
          <div className="footer-buttons">
            <button className="footer-btn" onClick={handleContactClick}>
              <p className="geist-mono-light">{currentContent.contact}</p>
              <i
                className={`fa-solid fa-arrow-right ${
                  contactClicked ? "rotate-45" : ""
                }`}
              ></i>
            </button>

            <button className="footer-btn" onClick={handleLinkedInClick}>
              <p className="geist-mono-light">{currentContent.linkedin}</p>
              <i
                className={`fa-solid fa-arrow-right ${
                  linkedinClicked ? "rotate-45" : ""
                }`}
              ></i>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );

  // On homepage the footer sits in the reveal-section flow — no portal needed
  // On all other pages, portal to body to escape framer-motion's stacking context
  if (isHomePage) return footerMarkup;
  return createPortal(footerMarkup, document.body);
}
